import { computed, onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { useDebounceFn } from "@vueuse/core";

import { useAccountsStore } from "@stores/accounts";
import { useContractsStore } from "@stores/contracts";
import { useCurrentUserStore } from "@stores/currentUser";
import { createFilters } from "@/utils/composables/filters/createFilters";
import {
  useFiltersUrlSync,
  arrayParam,
  stringParam,
  pageParam,
  type UrlFiltersState,
} from "@/utils/composables/filters/useFiltersUrlSync";

const urlParams = {
  status: arrayParam(),
  accounts: arrayParam(Number),
  owners: arrayParam(Number),
  search: stringParam(),
  page: pageParam(),
};

type ContractsUrlState = UrlFiltersState<typeof urlParams>;

export const useContractsFilters = () => {
  const accountsStore = useAccountsStore();
  const contractsStore = useContractsStore();

  const { accounts, owners } = storeToRefs(accountsStore);
  const { statusFilter, accountsFilter, ownersFilter, searchQuery, currentPage } = storeToRefs(contractsStore);
  const { clearFilters, setSearchQuery } = contractsStore;
  const { isEpisto } = storeToRefs(useCurrentUserStore());

  const accountOptions = computed(() => accounts.value?.map(({ name, id }) => ({ label: name, value: id })) || []);
  const ownerOptions = computed(() => owners.value.map(({ full_name, id }) => ({ label: full_name, value: id })));

  const filters = createFilters({
    filters: [
      {
        id: "status",
        label: "Status",
        options: [
          { label: "Active", value: "active" },
          { label: "Expired", value: "expired" },
        ],
        model: statusFilter,
      },
      {
        id: "accounts",
        label: "Account",
        hasSearch: true,
        options: accountOptions,
        model: accountsFilter,
      },
      {
        id: "owners",
        label: "Account owner",
        hasSearch: true,
        options: ownerOptions,
        model: ownersFilter,
        when: () => isEpisto.value,
      },
    ],
    search: () => contractsStore.fetch(1),
    clear: clearFilters,
  });

  const handleFilterChange = (filterId: string, value: string | number | boolean) => {
    if (filterId !== "status") {
      filters.handleFilterChange(filterId, value);
      return;
    }

    statusFilter.value = statusFilter.value.includes(value) ? [] : [value];
    contractsStore.fetch(1);
  };

  const serializeUrlState = (): ContractsUrlState => ({
    status: statusFilter.value,
    accounts: accountsFilter.value,
    owners: ownersFilter.value,
    search: searchQuery.value,
    page: currentPage.value,
  });

  const applyUrlState = (state: ContractsUrlState) => {
    statusFilter.value = state.status;
    accountsFilter.value = state.accounts;
    ownersFilter.value = state.owners;
    setSearchQuery(state.search);
    currentPage.value = state.page;
  };

  const { syncUrl, restoreFromUrl } = useFiltersUrlSync({
    params: urlParams,
    serialize: serializeUrlState,
    apply: applyUrlState,
    onExternalChange: async (state) => {
      applyUrlState(state);
      await contractsStore.fetch(state.page);
    },
  });

  restoreFromUrl();

  watch(
    [statusFilter, accountsFilter, ownersFilter, searchQuery, currentPage],
    useDebounceFn(syncUrl, 300),
    { deep: true },
  );

  onMounted(() => {
    if (!accounts.value?.length) accountsStore.fetch();
    if (isEpisto.value && !owners.value.length) accountsStore.fetchOwners();
  });

  return { ...filters, handleFilterChange };
};
